import { cn } from "@/lib/utils";

interface Column<T> {
  key: keyof T | string;
  header: string;
  width?: string | number | "auto";
  render?: (item: T, index: number) => React.ReactNode;
  className?: string;
}

interface TableProps<T> {
  data: T[];
  columns: Column<T>[];
  className?: string;
  emptyMessage?: string;
  isLoading?: boolean;
  fixedHeader?: boolean;
}

export function Table<T extends Record<string, any>>({
  data,
  columns,
  className,
  emptyMessage = "No data available",
  isLoading = false,
  fixedHeader = false
}: TableProps<T>) {
  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground text-xs">Loading...</div>
      </div>
    );
  }

  if (data.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-muted-foreground text-xs">{emptyMessage}</div>
      </div>
    );
  }

  if (fixedHeader) {
    return (
      <div className={cn("h-full flex flex-col", className)}>
        {/* Fixed Header */}
        <div className="flex-shrink-0 border-b border-border bg-muted/50">
          <table className="w-full table-fixed">
            <colgroup>
              {columns.map((column, index) => (
                <col
                  key={index}
                  style={{ 
                    width: column.width === "auto" ? "auto" : 
                           typeof column.width === "number" ? `${column.width * 4}px` : 
                           typeof column.width === "string" ? `${Number.parseInt(column.width, 10) * 4}px` :
                           "auto" 
                  }}
                />
              ))}
            </colgroup>
            <thead>
              <tr>
                {columns.map((column, index) => (
                  <th
                    key={index}
                    className={cn(
                      "px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider",
                      column.className
                    )}
                  >
                    {column.header}
                  </th>
                ))}
              </tr>
            </thead>
          </table>
        </div>
        
        {/* Scrollable Body */}
        <div className="flex-1 overflow-auto">
          <table className="w-full table-fixed">
            <colgroup>
              {columns.map((column, index) => (
                <col
                  key={index}
                  style={{ 
                    width: column.width === "auto" ? "auto" : 
                           typeof column.width === "number" ? `${column.width * 4}px` : 
                           typeof column.width === "string" ? `${Number.parseInt(column.width, 10) * 4}px` :
                           "auto" 
                  }}
                />
              ))}
            </colgroup>
            <tbody className="divide-y divide-border">
              {data.map((item, index) => (
                <tr
                  key={index}
                  className="hover:bg-muted/50 transition-colors"
                >
                  {columns.map((column, colIndex) => (
                    <td
                      key={colIndex}
                      className={cn(
                        "px-4 py-2 text-xs",
                        column.className
                      )}
                    >
                      {column.render
                        ? column.render(item, index)
                        : item[column.key as keyof T]
                      }
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  }

  // Regular table for non-fixed header mode
  return (
    <div className={cn("overflow-hidden", className)}>
      <table className="w-full">
        <thead>
          <tr className="border-b border-border bg-muted/50">
            {columns.map((column, index) => (
              <th
                key={index}
                className={cn(
                  "px-4 py-2 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider",
                  column.width && `w-${column.width}`,
                  column.className
                )}
              >
                {column.header}
              </th>
            ))}
          </tr>
        </thead>
        <tbody className="divide-y divide-border">
          {data.map((item, index) => (
            <tr
              key={index}
              className="hover:bg-muted/50 transition-colors"
            >
              {columns.map((column, colIndex) => (
                <td
                  key={colIndex}
                  className={cn(
                    "px-4 py-2 text-xs",
                    column.className
                  )}
                >
                  {column.render
                    ? column.render(item, index)
                    : item[column.key as keyof T]
                  }
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
